import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import productionApi from '../../services/productionApi';
import { useToast } from '../../contexts/ToastContext';
import './ProductionRunCard.css';

// Backend-exposed statuses for runs
const RUN_STATUS_OPTIONS = [
    { value: 'planned', label: 'Planned' },
    { value: 'roast', label: 'Roast' },
    { value: 'flavor', label: 'Flavor' },
    { value: 'grind', label: 'Grind' },
    { value: 'brew', label: 'Brew' },
    { value: 'mix', label: 'Mix' },
    { value: 'keg', label: 'Keg' },
    { value: 'can', label: 'Can' },
    { value: 'completed', label: 'Completed' }
];

function getTerminalStage(outputFormat, formats = []) {
    const format = formats.find(f => f.id === outputFormat);
    if (format && format.terminal_stage) {
        return format.terminal_stage.toLowerCase();
    }
    const TERMINAL_STAGE_BY_FORMAT = {
        cans_rtd: 'can',
        keg_5gal_rtd: 'keg',
        bottle_16oz_conc: 'keg',
        bottle_32oz_conc: 'keg',
        gallon_conc: 'keg',
    };
    return TERMINAL_STAGE_BY_FORMAT[outputFormat] ?? 'can';
}

const isRealOperator = (name) => {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    return !['string', 'system', 'undefined', 'null', '', 'none'].includes(lower);
};

const formatCompletionTimestamp = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        }) + ' · ' + date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch (e) {
        return dateString;
    }
};

const parseNotesAndCompletion = (notes) => {
    if (!notes) return { originalNotes: '', completion: null };

    const delimiter = notes.indexOf('\n[');
    if (delimiter !== -1) {
        const originalNotes = notes.substring(0, delimiter).trim();
        const completionPart = notes.substring(delimiter).trim();

        const regex = /\[([^\]]+)\]\s+Completed\s+by\s+([^:]+):\s*(.*)/;
        const match = completionPart.match(regex);

        if (match) {
            const timestamp = match[1];
            const completedBy = match[2].trim();
            const content = match[3].trim();

            let lossReason = null;
            if (content.startsWith("Loss Reason:")) {
                const lossText = content.substring("Loss Reason:".length).trim();
                const dotIndex = lossText.indexOf(".");
                if (dotIndex !== -1) {
                    lossReason = lossText.substring(0, dotIndex).trim();
                } else {
                    lossReason = lossText;
                }
            }

            return {
                originalNotes,
                completion: {
                    timestamp,
                    completedBy,
                    lossReason: lossReason || null
                }
            };
        }
    }

    const regex = /\[([^\]]+)\]\s+Completed\s+by\s+([^:]+):\s*(.*)/;
    const match = notes.match(regex);
    if (match) {
        const timestamp = match[1];
        const completedBy = match[2].trim();
        const content = match[3].trim();

        let lossReason = null;
        if (content.startsWith("Loss Reason:")) {
            const lossText = content.substring("Loss Reason:".length).trim();
            const dotIndex = lossText.indexOf(".");
            if (dotIndex !== -1) {
                lossReason = lossText.substring(0, dotIndex).trim();
            } else {
                lossReason = lossText;
            }
        }

        return {
            originalNotes: '',
            completion: {
                timestamp,
                completedBy,
                lossReason: lossReason || null
            }
        };
    }

    return {
        originalNotes: notes.trim(),
        completion: null
    };
};

export default function ProductionRunCard({ run = {}, onRunStatusUpdated, columnStatus, formats = [], onRefresh, isKanbanView = false }) {
    const initialStatus = (run.status || 'planned').toLowerCase();
    const [status, setStatus] = useState(initialStatus);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDetailsPopup, setShowDetailsPopup] = useState(false);
    const [cardActualQty, setCardActualQty] = useState('');
    const [cardLossReason, setCardLossReason] = useState('');

    const { showToast } = useToast();

    const parsedInfo = useMemo(() => parseNotesAndCompletion(run.notes), [run.notes]);

    const [recipeData, setRecipeData] = useState(null);
    const [isLoadingRecipe, setIsLoadingRecipe] = useState(false);
    const [recipeError, setRecipeError] = useState(false);

    const isRoastStage = (columnStatus === 'roast') || (status === 'roast');
    const isGrindStage = (columnStatus === 'grind') || (status === 'grind');
    const isBrewStage = (columnStatus === 'brew') || (status === 'brew');
    const isMixStage = (columnStatus === 'mix') || (status === 'mix');

    const mixRecipeDetails = useMemo(() => {
        if (!recipeData) return null;
        return {
            ingredients: recipeData.ingredients || recipeData.items || recipeData.coffee_requirements || [],
            mixRatio: recipeData.mix_ratio || recipeData.dilution_ratio || recipeData.ratio || null,
            concentrateRequired: recipeData.concentrate_required || recipeData.concentrate_kegs || run.concentrate_kegs || null,
            waterAdded: recipeData.water_added || recipeData.water_volume || recipeData.target_water || null,
            flavoring: recipeData.flavoring || recipeData.flavor_ingredients || recipeData.flavor_syrup || null,
            targetYield: recipeData.target_yield || recipeData.expected_yield || recipeData.final_volume || null,
            targetBrix: recipeData.target_brix || recipeData.brix_target || null,
            instructions: recipeData.instructions || recipeData.mixing_instructions || null
        };
    }, [recipeData, run]);

    const [brewFormData, setBrewFormData] = useState({
        water_temp_notes: '',
        ext1_yield: '',
        ext1_brix: '',
        ext2_yield: '',
        ext2_brix: '',
        ext3_yield: '',
        ext3_brix: '',
        observations: ''
    });
    const [isSavingBrewData, setIsSavingBrewData] = useState(false);

    const weatherData = useMemo(() => {
        return recipeData?.weather || recipeData?.weather_snapshot || run.weather || run.weather_snapshot || null;
    }, [recipeData, run]);

    useEffect(() => {
        if (showDetailsPopup && isBrewStage) {
            const brewStageObj = Array.isArray(run.stage_data)
                ? run.stage_data.find(s => s.stage === 'brew')
                : null;
            const source = brewStageObj?.data || run.brew_data || run.brewData || recipeData?.brew_data || {};
            const notes = brewStageObj?.notes || source.observations || source.notes || run.brew_notes || '';

            // Check extractions list
            const extractionsList = Array.isArray(source.extractions) ? source.extractions : [];
            const ext1 = extractionsList.find(e => e.pass === 1) || {};
            const ext2 = extractionsList.find(e => e.pass === 2) || {};
            const ext3 = extractionsList.find(e => e.pass === 3) || {};

            setBrewFormData({
                water_temp_notes: source.water_temperature_notes ?? source.water_temp_notes ?? run.water_temp_notes ?? '',
                ext1_yield: ext1.yield_value ?? source.ext1_yield ?? source.extraction_1_yield ?? source.extraction_1?.yield ?? run.ext1_yield ?? '',
                ext1_brix: ext1.brix ?? source.ext1_brix ?? source.extraction_1_brix ?? source.extraction_1?.brix ?? run.ext1_brix ?? '',
                ext2_yield: ext2.yield_value ?? source.ext2_yield ?? source.extraction_2_yield ?? source.extraction_2?.yield ?? run.ext2_yield ?? '',
                ext2_brix: ext2.brix ?? source.ext2_brix ?? source.extraction_2_brix ?? source.extraction_2?.brix ?? run.ext2_brix ?? '',
                ext3_yield: ext3.yield_value ?? source.ext3_yield ?? source.extraction_3_yield ?? source.extraction_3?.yield ?? run.ext3_yield ?? '',
                ext3_brix: ext3.brix ?? source.ext3_brix ?? source.extraction_3_brix ?? source.extraction_3?.brix ?? run.ext3_brix ?? '',
                observations: notes
            });
        }
    }, [showDetailsPopup, isBrewStage, run, recipeData]);

    const handleSaveBrewData = useCallback(async (e) => {
        e.preventDefault();
        if (!run?.id) return;

        const extractions = [];
        if (brewFormData.ext1_yield !== '' || brewFormData.ext1_brix !== '') {
            extractions.push({
                pass: 1,
                yield_value: brewFormData.ext1_yield !== '' ? Number(brewFormData.ext1_yield) : null,
                brix: brewFormData.ext1_brix !== '' ? Number(brewFormData.ext1_brix) : null
            });
        }
        if (brewFormData.ext2_yield !== '' || brewFormData.ext2_brix !== '') {
            extractions.push({
                pass: 2,
                yield_value: brewFormData.ext2_yield !== '' ? Number(brewFormData.ext2_yield) : null,
                brix: brewFormData.ext2_brix !== '' ? Number(brewFormData.ext2_brix) : null
            });
        }
        if (brewFormData.ext3_yield !== '' || brewFormData.ext3_brix !== '') {
            extractions.push({
                pass: 3,
                yield_value: brewFormData.ext3_yield !== '' ? Number(brewFormData.ext3_yield) : null,
                brix: brewFormData.ext3_brix !== '' ? Number(brewFormData.ext3_brix) : null
            });
        }

        const payload = {
            data: {
                water_temp_notes: brewFormData.water_temp_notes,
                water_temperature_notes: brewFormData.water_temp_notes,
                extractions: extractions,
                extraction_1: {
                    yield: brewFormData.ext1_yield !== '' ? Number(brewFormData.ext1_yield) : null,
                    brix: brewFormData.ext1_brix !== '' ? Number(brewFormData.ext1_brix) : null
                },
                extraction_2: {
                    yield: brewFormData.ext2_yield !== '' ? Number(brewFormData.ext2_yield) : null,
                    brix: brewFormData.ext2_brix !== '' ? Number(brewFormData.ext2_brix) : null
                },
                extraction_3: {
                    yield: brewFormData.ext3_yield !== '' ? Number(brewFormData.ext3_yield) : null,
                    brix: brewFormData.ext3_brix !== '' ? Number(brewFormData.ext3_brix) : null
                },
                ext1_yield: brewFormData.ext1_yield !== '' ? Number(brewFormData.ext1_yield) : null,
                ext1_brix: brewFormData.ext1_brix !== '' ? Number(brewFormData.ext1_brix) : null,
                ext2_yield: brewFormData.ext2_yield !== '' ? Number(brewFormData.ext2_yield) : null,
                ext2_brix: brewFormData.ext2_brix !== '' ? Number(brewFormData.ext2_brix) : null,
                ext3_yield: brewFormData.ext3_yield !== '' ? Number(brewFormData.ext3_yield) : null,
                ext3_brix: brewFormData.ext3_brix !== '' ? Number(brewFormData.ext3_brix) : null,
                observations: brewFormData.observations
            },
            notes: brewFormData.observations
        };

        try {
            setIsSavingBrewData(true);
            await productionApi.saveStageData(run.id, payload, 'brew');
            showToast('Brew stage data saved successfully!', 'success');
            if (onRefresh) {
                await onRefresh();
            } else if (onRunStatusUpdated) {
                await onRunStatusUpdated(run, status, {});
            }
        } catch (err) {
            console.error('Failed to save brew stage data:', err);
            const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save brew stage data.';
            showToast(errorMsg, 'error');
        } finally {
            setIsSavingBrewData(false);
        }
    }, [run, brewFormData, showToast, onRunStatusUpdated, status, onRefresh]);

    const grindSizeValue = useMemo(() => {
        if (!run) return null;

        const candidateValues = [
            run.grind_size,
            run.grind_setting,
            run.grind_size_setting,
            run.grindSize,
            run.grindSetting,
            run.grind,
            run.product?.grind_size,
            run.product?.grind_setting,
            run.product?.grind_size_setting,
            run.recipe?.grind_size,
            run.recipe?.grind_setting,
            recipeData?.grind_size,
            recipeData?.grind_setting,
            recipeData?.grind_size_setting,
            recipeData?.grindSize,
            recipeData?.grindSetting,
            recipeData?.grind,
            recipeData?.recipe?.grind_size,
            recipeData?.recipe?.grind_setting,
            recipeData?.recipe?.grind_size_setting,
            recipeData?.product?.grind_size,
            recipeData?.product?.grind_setting,
            recipeData?.product?.grind_size_setting,
        ];

        for (const val of candidateValues) {
            if (val != null && typeof val === 'string' && val.trim() !== '') {
                return val.trim();
            }
            if (typeof val === 'number') {
                return String(val);
            }
        }

        return null;
    }, [run, recipeData]);

    useEffect(() => {
        let isCancelled = false;

        if ((isRoastStage || isGrindStage || isBrewStage || isMixStage) && run?.id) {
            setIsLoadingRecipe(true);
            setRecipeError(false);

            productionApi.getRecipe(run.id)
                .then((data) => {
                    if (!isCancelled) {
                        setRecipeData(data);
                    }
                })
                .catch((err) => {
                    if (!isCancelled) {
                        console.error(`Error fetching recipe for run ${run.id}:`, err);
                        setRecipeError(true);
                    }
                })
                .finally(() => {
                    if (!isCancelled) {
                        setIsLoadingRecipe(false);
                    }
                });
        }

        return () => {
            isCancelled = true;
        };
    }, [isRoastStage, isGrindStage, isBrewStage, isMixStage, run?.id]);

    const coffeeOrigin = useMemo(() => {
        if (run.origin && typeof run.origin === 'string' && run.origin.trim()) {
            return run.origin;
        }
        if (recipeData?.origin && typeof recipeData.origin === 'string' && recipeData.origin.trim()) {
            return recipeData.origin;
        }
        if (recipeData?.coffee_origin && typeof recipeData.coffee_origin === 'string' && recipeData.coffee_origin.trim()) {
            return recipeData.coffee_origin;
        }
        return null;
    }, [run.origin, recipeData]);

    const coffeeRequirements = useMemo(() => {
        if (!recipeData) return [];
        const reqs = recipeData.coffee_requirements || recipeData.coffeeRequirements || recipeData.items || [];
        if (!Array.isArray(reqs)) return [];
        return reqs;
    }, [recipeData]);

    useEffect(() => {
        setStatus((run.status || 'planned').toLowerCase());
    }, [run.status]);

    useEffect(() => {
        if (pendingStatus === 'completed') {
            const planned = run.planned_quantity ?? run.quantity ?? 0;
            setCardActualQty(String(planned));
            setCardLossReason('');
        }
    }, [pendingStatus, run.planned_quantity, run.quantity]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && showDetailsPopup) {
                setShowDetailsPopup(false);
            }
        };

        if (showDetailsPopup) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showDetailsPopup]);


    const handleConfirmStatusChange = useCallback(async () => {
        if (!pendingStatus) return;

        try {
            setIsSubmitting(true);
            const options = pendingStatus === 'completed' ? {
                actualQuantity: cardActualQty !== '' ? Number(cardActualQty) : (run.planned_quantity ?? run.quantity ?? 0),
                lossReason: cardLossReason
            } : {};
            if (onRunStatusUpdated) {
                await onRunStatusUpdated(run, pendingStatus, options);
            }
            setStatus(pendingStatus);
        } finally {
            setIsSubmitting(false);
            setShowConfirmModal(false);
            setPendingStatus(null);
            setCardActualQty('');
            setCardLossReason('');
        }
    }, [onRunStatusUpdated, pendingStatus, run, cardActualQty, cardLossReason]);

    const handleCancelStatusChange = useCallback(() => {
        setShowConfirmModal(false);
        setPendingStatus(null);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showConfirmModal) {
                if (e.key === 'Escape') {
                    handleCancelStatusChange();
                } else if (e.key === 'Enter' && !isSubmitting) {
                    handleConfirmStatusChange();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showConfirmModal, isSubmitting, handleCancelStatusChange, handleConfirmStatusChange]);

    const getStatusLabel = () => {
        const option = RUN_STATUS_OPTIONS.find(opt => opt.value === status);
        return option ? option.label : status;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const outputUnit = run.output_unit || 'cans';
    const plannedQuantity = run.planned_quantity || 0;
    const producedQuantity = run.actual_quantity ?? run.produced_quantity ?? 0;
    const formatQuantity = (quantity) => `${(quantity || 0).toLocaleString()} ${outputUnit}`;

    const { efficiency, isOverProduction, isUnderProduction } = useMemo(() => {
        const planned = plannedQuantity;
        const produced = producedQuantity;
        const eff = planned > 0 ? (produced / planned) * 100 : 0;

        return {
            efficiency: eff,
            isOverProduction: produced > planned,
            isUnderProduction: produced < planned && status === 'completed'
        };
    }, [plannedQuantity, producedQuantity, status]);

    const modalTitle = pendingStatus === 'completed'
        ? '✅ Complete Production Run'
        : 'Confirm Status Change';

    const modalBody = pendingStatus === 'completed'
        ? (
            <div className="completion-modal-form">
                <p className="completion-question-text">
                    How many did you actually get?
                </p>
                <div className="planned-hint">
                    Planned Quantity: <strong>{run.planned_quantity ?? run.quantity ?? 0}</strong> {run.output_unit || 'units'}
                </div>
                <div className="form-group">
                    <label className="form-label">Actual Final Yield</label>
                    <input
                        type="number"
                        min="0"
                        className="completion-input"
                        value={cardActualQty}
                        onChange={(e) => setCardActualQty(e.target.value)}
                        placeholder={String(run.planned_quantity ?? run.quantity ?? 0)}
                        autoFocus
                    />
                </div>
                <div className="form-group" >
                    <label className="form-label">Loss Reason <span className="label-optional">(Optional)</span></label>
                    <textarea
                        className="completion-textarea"
                        rows="3"
                        value={cardLossReason}
                        onChange={(e) => setCardLossReason(e.target.value)}
                        placeholder="Explain any losses (e.g. 15 cans lost during seam calibration)..."
                    />
                </div>
            </div>
        ) : (
            <>
                <p>
                    You are about to change the run status from <strong>{getStatusLabel()}</strong> to{' '}
                    <strong>{RUN_STATUS_OPTIONS.find(o => o.value === pendingStatus)?.label || pendingStatus}</strong>.
                </p>
                <p className="modal-subtext">Are you sure you want to continue?</p>
            </>
        );

    return (
        <div
            className={`production-run-card compact ${columnStatus ? `stage-${columnStatus}` : ''} ${!isKanbanView ? 'list-view-card' : ''}`}
            onClick={() => setShowDetailsPopup(true)}
            data-run-id={run.id}
        >
            <div className="card-header-compact">
                <div className="header-top-row">
                    <div className="product-name">{run.flavor}</div>
                    {run.output_format_name && (
                        <div className="run-format-name">{run.output_format_name}</div>
                    )}
                </div>
                {columnStatus && columnStatus === getTerminalStage(run.output_format, formats) && status !== 'completed' && (
                    <div className="badge-row">
                        <div className="ready-to-complete-badge">Ready to Complete</div>
                    </div>
                )}
            </div>

            <div className="run-card-metrics-wrapper">
                <div className="run-card-metrics-grid">
                    <div className="metric-item">
                        <label>Planned</label>
                        <span className="metric-value">{formatQuantity(plannedQuantity)}</span>
                    </div>
                    <div className="metric-item">
                        <label>Produced</label>
                        <span className={`metric-value ${isOverProduction ? 'over-production' : isUnderProduction ? 'under-production' : ''}`}>
                            {formatQuantity(producedQuantity)}
                        </span>
                    </div>
                    <div className="metric-item efficiency-item">
                        <label>Efficiency</label>
                        <span className={`metric-value ${efficiency > 100 ? 'over-production' : efficiency < 85 ? 'under-production' : 'good-efficiency'}`}>
                            {efficiency.toFixed(1)}%
                        </span>
                    </div>
                </div>

                <div className="schedule-section">
                    <div className="schedule-item">
                        <label>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginRight: '4px' }}>
                                <path d="M10 2H9V1.5C9 1.22386 8.77614 1 8.5 1C8.22386 1 8 1.22386 8 1.5V2H4V1.5C4 1.22386 3.77614 1 3.5 1C3.22386 1 3 1.22386 3 1.5V2H2C1.44772 2 1 2.44772 1 3V10C1 10.5523 1.44772 11 2 11H10C10.5523 11 11 10.5523 11 10V3C11 2.44772 10.5523 2 10 2ZM10 10H2V5H10V10Z" fill="currentColor" />
                            </svg>
                            Schedule Start
                        </label>
                        <span className={`schedule-value ${!run.scheduled_start_time ? 'not-set' : ''}`}>
                            {run.scheduled_start_time
                                ? new Date(run.scheduled_start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '—'}
                        </span>
                    </div>
                    <div className="schedule-item">
                        <label>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginRight: '4px' }}>
                                <path d="M10 2H9V1.5C9 1.22386 8.77614 1 8.5 1C8.22386 1 8 1.22386 8 1.5V2H4V1.5C4 1.22386 3.77614 1 3.5 1C3.22386 1 3 1.22386 3 1.5V2H2C1.44772 2 1 2.44772 1 3V10C1 10.5523 1.44772 11 2 11H10C10.5523 11 11 10.5523 11 10V3C11 2.44772 10.5523 2 10 2ZM10 10H2V5H10V10Z" fill="currentColor" />
                            </svg>
                            Schedule End
                        </label>
                        <span className={`schedule-value ${!run.scheduled_end_time ? 'not-set' : ''}`}>
                            {run.scheduled_end_time
                                ? new Date(run.scheduled_end_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '—'}
                        </span>
                    </div>
                </div>
            </div>

            {isRoastStage && (
                <div className="roast-details-section">
                    {isLoadingRecipe ? (
                        <div className="roast-recipe-skeleton">
                            <div className="skeleton-line short"></div>
                            <div className="skeleton-line medium"></div>
                        </div>
                    ) : recipeError ? (
                        <div className="roast-recipe-content">
                            <div className="roast-detail-group">
                                <span className="roast-detail-label">Coffee Origin</span>
                                <span className="roast-detail-value not-specified">{coffeeOrigin || 'Not specified'}</span>
                            </div>
                            <div className="roast-detail-group">
                                <span className="roast-detail-label">Recipe Details</span>
                                <span className="roast-detail-value not-specified">Unavailable</span>
                            </div>
                        </div>
                    ) : (
                        <div className="roast-recipe-content">
                            <div className="roast-detail-group">
                                <span className="roast-detail-label">Coffee Origin</span>
                                <span className="roast-detail-value">{coffeeOrigin || 'Not specified'}</span>
                            </div>

                            {coffeeRequirements.length === 1 ? (
                                <div className="roast-detail-group">
                                    <span className="roast-detail-label">Coffee Weight</span>
                                    <span className="roast-detail-value">
                                        {coffeeRequirements[0].total_quantity_for_run ?? coffeeRequirements[0].quantity ?? '—'}{' '}
                                        {coffeeRequirements[0].unit || ''}
                                        {coffeeRequirements[0].ingredient_name && (
                                            <span className="roast-detail-subtext">({coffeeRequirements[0].ingredient_name})</span>
                                        )}
                                    </span>
                                </div>
                            ) : coffeeRequirements.length > 1 ? (
                                <div className="roast-detail-group">
                                    <span className="roast-detail-label">Coffee Requirements</span>
                                    <div className="coffee-reqs-list">
                                        {coffeeRequirements.map((req, idx) => (
                                            <div key={idx} className="coffee-req-item">
                                                <span className="coffee-req-name">{req.ingredient_name || 'Coffee Requirement'}</span>
                                                <span className="coffee-req-weight">
                                                    {req.total_quantity_for_run ?? req.quantity ?? '—'} {req.unit || ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="roast-detail-group">
                                    <span className="roast-detail-label">Coffee Weight</span>
                                    <span className="roast-detail-value not-specified">No coffee requirements</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isGrindStage && (
                <div className="grind-details-section">
                    {isLoadingRecipe ? (
                        <div className="roast-recipe-skeleton">
                            <div className="skeleton-line short"></div>
                            <div className="skeleton-line medium"></div>
                        </div>
                    ) : (
                        <div className="grind-detail-group">
                            <span className="grind-detail-label">Grind Size</span>
                            <span className={`grind-detail-value ${!grindSizeValue ? 'not-specified' : ''}`}>
                                {grindSizeValue || (recipeError ? 'Recipe unavailable' : 'Not specified')}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {isBrewStage && (
                <div className="brew-card-action">
                    <button
                        type="button"
                        className="btn-brew-details"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDetailsPopup(true);
                        }}
                    >
                        ☕ View Brew Details & Stage Log →
                    </button>
                </div>
            )}

            {isMixStage && (
                <div className="mix-card-action">
                    <button
                        type="button"
                        className="btn-mix-details"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDetailsPopup(true);
                        }}
                    >
                        🥤 View Mix Recipe & Details →
                    </button>
                </div>
            )}

            {showDetailsPopup && createPortal(
                <>
                    <div className="details-popup-overlay" onClick={() => setShowDetailsPopup(false)} />
                    <div className="details-popup-card" onClick={(e) => e.stopPropagation()}>
                        <div className="popup-header">
                            <h3>Production Run Details</h3>
                            <button
                                type="button"
                                className="popup-close"
                                onClick={() => setShowDetailsPopup(false)}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="popup-body">
                            <div className="popup-section">
                                <h4>Product Information</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Product Name:</span>
                                    <span className="detail-value">{run.flavor}</span>
                                </div>
                                {run.output_format_name && (
                                    <div className="detail-row">
                                        <span className="detail-label">Output Format:</span>
                                        <span className="detail-value">{run.output_format_name}</span>
                                    </div>
                                )}
                                {run.concentrate_kegs != null && (
                                    <div className="detail-row">
                                        <span className="detail-label">Concentrate Required:</span>
                                        <span className="detail-value">{Number(run.concentrate_kegs).toFixed(2)} kegs</span>
                                    </div>
                                )}
                            </div>

                            {isRoastStage && (
                                <div className="popup-section">
                                    <h4>Roast Recipe & Coffee Details</h4>
                                    <div className="detail-row">
                                        <span className="detail-label">Coffee Origin:</span>
                                        <span className="detail-value">{coffeeOrigin || 'Origin not specified'}</span>
                                    </div>
                                    {isLoadingRecipe ? (
                                        <div className="detail-row">
                                            <span className="detail-label">Coffee Weight:</span>
                                            <span className="detail-value">Loading recipe data...</span>
                                        </div>
                                    ) : recipeError ? (
                                        <div className="detail-row">
                                            <span className="detail-label">Coffee Weight:</span>
                                            <span className="detail-value" style={{ color: '#dc2626' }}>Recipe unavailable</span>
                                        </div>
                                    ) : coffeeRequirements.length > 0 ? (
                                        coffeeRequirements.map((req, idx) => (
                                            <div key={idx} className="detail-row">
                                                <span className="detail-label">{req.ingredient_name || `Coffee Requirement ${idx + 1}`}:</span>
                                                <span className="detail-value">
                                                    {req.total_quantity_for_run ?? req.quantity ?? '—'} {req.unit || ''}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="detail-row">
                                            <span className="detail-label">Coffee Weight:</span>
                                            <span className="detail-value">No coffee requirements</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isGrindStage && (
                                <div className="popup-section">
                                    <h4>Grind Details</h4>
                                    <div className="detail-row">
                                        <span className="detail-label">Grind Size:</span>
                                        <span className={`detail-value ${!grindSizeValue ? 'not-specified' : ''}`}>
                                            {isLoadingRecipe
                                                ? 'Loading...'
                                                : grindSizeValue || (recipeError ? 'Recipe unavailable' : 'Not specified')}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {isBrewStage && (
                                <>
                                    <div className="popup-section brew-recipe-section">
                                        <h4>☕ Full Brew Recipe</h4>
                                        {isLoadingRecipe ? (
                                            <p className="loading-text">Loading full recipe details...</p>
                                        ) : recipeError ? (
                                            <p className="error-text">Recipe details unavailable</p>
                                        ) : recipeData ? (
                                            <div className="recipe-details-box">
                                                {coffeeRequirements.length > 0 && (
                                                    <div className="recipe-subgroup">
                                                        <span className="recipe-subhead">Coffee Requirements:</span>
                                                        {coffeeRequirements.map((req, idx) => (
                                                            <div key={idx} className="recipe-row">
                                                                <span>{req.ingredient_name || 'Coffee'}:</span>
                                                                <strong>{req.total_quantity_for_run ?? req.quantity ?? '—'} {req.unit || ''}</strong>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {(recipeData.water_volume || recipeData.target_water || recipeData.water) && (
                                                    <div className="recipe-row">
                                                        <span>Target Water Volume:</span>
                                                        <strong>{recipeData.water_volume || recipeData.target_water || recipeData.water}</strong>
                                                    </div>
                                                )}
                                                {(recipeData.target_yield || recipeData.expected_yield) && (
                                                    <div className="recipe-row">
                                                        <span>Target Yield:</span>
                                                        <strong>{recipeData.target_yield || recipeData.expected_yield}</strong>
                                                    </div>
                                                )}
                                                {(recipeData.target_brix || recipeData.brix_target) && (
                                                    <div className="recipe-row">
                                                        <span>Target Brix:</span>
                                                        <strong>{recipeData.target_brix || recipeData.brix_target}</strong>
                                                    </div>
                                                )}
                                                {recipeData.instructions && (
                                                    <div className="recipe-subgroup">
                                                        <span className="recipe-subhead">Instructions:</span>
                                                        <p className="recipe-instructions-text">{recipeData.instructions}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="empty-text">No recipe data returned for this run</p>
                                        )}
                                    </div>

                                    <div className="popup-section brew-weather-section">
                                        <h4>🌤️ Weather Snapshot (Brew Stage)</h4>
                                        {weatherData ? (
                                            <div className="weather-grid">
                                                <div className="weather-card">
                                                    <span className="weather-label">Temperature</span>
                                                    <span className="weather-value">{weatherData.temperature || weatherData.temp_f || weatherData.temp_c || '—'}</span>
                                                </div>
                                                <div className="weather-card">
                                                    <span className="weather-label">Humidity</span>
                                                    <span className="weather-value">{weatherData.humidity || weatherData.humidity_percent || '—'}</span>
                                                </div>
                                                <div className="weather-card">
                                                    <span className="weather-label">Conditions</span>
                                                    <span className="weather-value">{weatherData.conditions || weatherData.pressure || weatherData.summary || '—'}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="weather-unavailable">Weather snapshot not available for this run</p>
                                        )}
                                    </div>

                                    <div className="popup-section brew-form-section">
                                        <h4>🧪 Brew Stage Operator Log</h4>
                                        <form onSubmit={handleSaveBrewData} className="brew-data-form">
                                            <div className="form-group">
                                                <label className="form-label">Water Temperature</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    placeholder="Record water temperature, initial temperature, hold temperature, or other temperature observations..."
                                                    value={brewFormData.water_temp_notes}
                                                    onChange={(e) => setBrewFormData(prev => ({ ...prev, water_temp_notes: e.target.value }))}
                                                />
                                            </div>

                                            <div className="extractions-title">Extraction Results</div>
                                            <div className="extractions-grid">
                                                <div className="extraction-card">
                                                    <h5>1st Extraction</h5>
                                                    <div className="extraction-inputs">
                                                        <div className="input-field">
                                                            <label>Yield</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 50"
                                                                value={brewFormData.ext1_yield}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext1_yield: e.target.value }))}
                                                            />
                                                        </div>
                                                        <div className="input-field">
                                                            <label>Brix</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 8.5"
                                                                value={brewFormData.ext1_brix}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext1_brix: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="extraction-card">
                                                    <h5>2nd Extraction</h5>
                                                    <div className="extraction-inputs">
                                                        <div className="input-field">
                                                            <label>Yield</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 35"
                                                                value={brewFormData.ext2_yield}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext2_yield: e.target.value }))}
                                                            />
                                                        </div>
                                                        <div className="input-field">
                                                            <label>Brix</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 4.2"
                                                                value={brewFormData.ext2_brix}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext2_brix: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="extraction-card">
                                                    <h5>3rd Extraction</h5>
                                                    <div className="extraction-inputs">
                                                        <div className="input-field">
                                                            <label>Yield</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 20"
                                                                value={brewFormData.ext3_yield}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext3_yield: e.target.value }))}
                                                            />
                                                        </div>
                                                        <div className="input-field">
                                                            <label>Brix</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                className="form-control"
                                                                placeholder="e.g. 1.8"
                                                                value={brewFormData.ext3_brix}
                                                                onChange={(e) => setBrewFormData(prev => ({ ...prev, ext3_brix: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group" >
                                                <label className="form-label">Notes / Observations</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    placeholder="Enter free-text brewing observations or quality notes..."
                                                    value={brewFormData.observations}
                                                    onChange={(e) => setBrewFormData(prev => ({ ...prev, observations: e.target.value }))}
                                                />
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn-save-brew-data"
                                                disabled={isSavingBrewData}
                                            >
                                                {isSavingBrewData ? (
                                                    <>
                                                        <span className="spinner-mini"></span>
                                                        Saving Brew Stage Data...
                                                    </>
                                                ) : (
                                                    '💾 Save Brew Stage Data'
                                                )}
                                            </button>
                                        </form>
                                    </div>
                                </>
                            )}

                            {isMixStage && (
                                <div className="popup-section mix-recipe-section">
                                    {isLoadingRecipe ? (
                                        <div className="roast-recipe-skeleton">
                                            <div className="skeleton-line short"></div>
                                            <div className="skeleton-line medium"></div>
                                            <div className="skeleton-line medium"></div>
                                        </div>
                                    ) : recipeError ? (
                                        <p className="error-text">Mix recipe unavailable</p>
                                    ) : recipeData ? (
                                        <div className="mix-recipe-card">
                                            <h4 className="mix-recipe-header">
                                                🥤 Full Mix Recipe & Formulation
                                            </h4>

                                            {mixRecipeDetails?.concentrateRequired != null && (
                                                <div className="concentrate-highlight-metric">
                                                    <span className="concentrate-label">Concentrate Required</span>
                                                    <span className="concentrate-value">
                                                        {Number(mixRecipeDetails.concentrateRequired).toFixed(2)} <span className="concentrate-unit">kegs</span>
                                                    </span>
                                                </div>
                                            )}

                                            <div className="mix-specs-grid">
                                                {mixRecipeDetails?.mixRatio && (
                                                    <div className="spec-item">
                                                        <span className="spec-label">Mixing / Dilution Ratio</span>
                                                        <span className="spec-value">{mixRecipeDetails.mixRatio}</span>
                                                    </div>
                                                )}
                                                {mixRecipeDetails?.waterAdded && (
                                                    <div className="spec-item">
                                                        <span className="spec-label">Water Volume Added</span>
                                                        <span className="spec-value">{mixRecipeDetails.waterAdded}</span>
                                                    </div>
                                                )}
                                                {mixRecipeDetails?.targetYield && (
                                                    <div className="spec-item">
                                                        <span className="spec-label">Target Final Yield</span>
                                                        <span className="spec-value">{mixRecipeDetails.targetYield}</span>
                                                    </div>
                                                )}
                                                {mixRecipeDetails?.targetBrix && (
                                                    <div className="spec-item">
                                                        <span className="spec-label">Target Brix / Sweetness</span>
                                                        <span className="spec-value">{mixRecipeDetails.targetBrix}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {coffeeRequirements.length > 0 && (
                                                <div className="recipe-subgroup-premium">
                                                    <span className="recipe-subhead-premium">
                                                        ⚖️ Base Ingredients & Concentrate
                                                    </span>
                                                    <div className="ingredients-list-container">
                                                        {coffeeRequirements.map((req, idx) => (
                                                            <div key={idx} className="ingredient-leader-row">
                                                                <span className="ingredient-name">{req.ingredient_name || 'Ingredient'}</span>
                                                                <span className="leader-dots"></span>
                                                                <span className="ingredient-quantity">
                                                                    {req.total_quantity_for_run ?? req.quantity ?? '—'} {req.unit || ''}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {mixRecipeDetails?.flavoring && (
                                                <div className="recipe-subgroup-premium">
                                                    <span className="recipe-subhead-premium">
                                                        🌿 Flavoring & Additives
                                                    </span>
                                                    <div className="ingredient-leader-row">
                                                        <span className="ingredient-name">Flavor Formulation</span>
                                                        <span className="leader-dots"></span>
                                                        <span className="ingredient-quantity" style={{ fontFamily: 'sans-serif' }}>
                                                            {typeof mixRecipeDetails.flavoring === 'string' ? mixRecipeDetails.flavoring : JSON.stringify(mixRecipeDetails.flavoring)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {mixRecipeDetails?.instructions && (
                                                <div className="recipe-subgroup-premium">
                                                    <span className="recipe-subhead-premium">
                                                        📝 Mixing Instructions
                                                    </span>
                                                    <p className="instructions-card">{mixRecipeDetails.instructions}</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="empty-text">No recipe data returned for this mix run</p>
                                    )}
                                </div>
                            )}

                            <div className="popup-section">
                                <h4>Status Information</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Current Status:</span>
                                    <span className={`status-badge ${status}`}>{getStatusLabel()}</span>
                                </div>
                            </div>

                            <div className="popup-section">
                                <h4>Quantities</h4>
                                <div className="quantities-grid">
                                    <div className="quantity-card">
                                        <span className="quantity-label">Planned Quantity</span>
                                        <span className="quantity-value">{formatQuantity(plannedQuantity)}</span>
                                    </div>
                                    <div className="quantity-card">
                                        <span className="quantity-label">Produced Quantity</span>
                                        <span className={`quantity-value ${isOverProduction ? 'over-production' : isUnderProduction ? 'under-production' : ''}`}>
                                            {formatQuantity(producedQuantity)}
                                        </span>
                                    </div>
                                    <div className="quantity-card full-width">
                                        <span className="quantity-label">Efficiency</span>
                                        <span className={`quantity-value ${efficiency > 100 ? 'over-production' : efficiency < 85 ? 'under-production' : 'good-efficiency'}`}>
                                            {efficiency.toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="popup-section">
                                <h4>Schedule</h4>
                                <div className="schedule-grid">
                                    <div className="schedule-card">
                                        <span className="schedule-label">Scheduled Start</span>
                                        <span className="schedule-value">{formatDateTime(run.scheduled_start_time)}</span>
                                    </div>
                                    <div className="schedule-card">
                                        <span className="schedule-label">Scheduled End</span>
                                        <span className="schedule-value">{formatDateTime(run.scheduled_end_time)}</span>
                                    </div>
                                </div>
                            </div>

                            {status === 'completed' && (
                                <div className="popup-section">
                                    <h4>📋 Production Completion Summary</h4>
                                    <div className="completion-summary-card">
                                        <div className="completion-summary-grid">
                                            <div className="summary-field">
                                                <span className="summary-field-label">Product</span>
                                                <span className="summary-field-value">{run.flavor}</span>
                                            </div>
                                            <div className="summary-field">
                                                <span className="summary-field-label">Actual Yield</span>
                                                <span className="summary-field-valueHighlight">{formatQuantity(producedQuantity)}</span>
                                            </div>
                                            {run.concentrate_kegs != null && (
                                                <div className="summary-field">
                                                    <span className="summary-field-label">Concentrate Used</span>
                                                    <span className="summary-field-value">{Number(run.concentrate_kegs).toFixed(2)} kegs</span>
                                                </div>
                                            )}
                                            {run.completed_at && (
                                                <div className="summary-field">
                                                    <span className="summary-field-label">Completed At</span>
                                                    <span className="summary-field-value">{formatCompletionTimestamp(run.completed_at)}</span>
                                                </div>
                                            )}
                                            {parsedInfo.completion && isRealOperator(parsedInfo.completion.completedBy) && (
                                                <div className="summary-field">
                                                    <span className="summary-field-label">Completed By</span>
                                                    <span className="summary-field-value">{parsedInfo.completion.completedBy}</span>
                                                </div>
                                            )}
                                            {parsedInfo.completion?.lossReason && (
                                                <div className="summary-field full-width">
                                                    <span className="summary-field-label">Loss Reason</span>
                                                    <span className="summary-field-value loss-reason-text">{parsedInfo.completion.lossReason}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {((status === 'completed' && parsedInfo.originalNotes) || (status !== 'completed' && run.notes)) && (
                                <div className="popup-section">
                                    <h4>Notes</h4>
                                    <p className="notes-content">{status === 'completed' ? parsedInfo.originalNotes : run.notes}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {showConfirmModal && (
                <>
                    <div className="modal-overlay" onClick={handleCancelStatusChange} />
                    <div className="confirmation-modal">
                        <div className="modal-header">
                            <h3>{modalTitle}</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={handleCancelStatusChange}
                                disabled={isSubmitting}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            {modalBody}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCancelStatusChange}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-btn modal-btn-confirm"
                                onClick={handleConfirmStatusChange}
                                disabled={isSubmitting}
                            >
                                {isSubmitting
                                    ? (pendingStatus === 'completed' ? 'Completing...' : 'Updating...')
                                    : (pendingStatus === 'completed' ? 'Complete Run' : 'Confirm')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
